/*
 * Copyright (c) 2024, King Living
 * Swipeable Cart Item Component
 *
 * Wraps ProductItem to enable swipe-to-delete on mobile
 */

import React, {useCallback, useRef, useState, useEffect} from 'react';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {Box, useBreakpointValue, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

const SWIPE_THRESHOLD = 100; // Minimum distance to reveal delete button

/**
 * Swipeable Cart Item
 * Enables swipe-to-delete functionality on mobile devices
 */
const SwipeableItem = ({
  children,
  onSwipeDelete,
  isFirstItem = false,
}) => {
  const intl = useIntl();
  const isMobile = useBreakpointValue({base: true, lg: false});
  const styles = useMultiStyleConfig('CartFlyout', {});
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [translateX, setTranslateX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteTimerRef = useRef(null);
  const resumeAnimationTimerRef = useRef(null);
  const hasInitiatedDeleteRef = useRef(false);

  // Reset position when not dragging
  useEffect(() => {
    // Skip if already processing delete
    if (hasInitiatedDeleteRef.current) {
      return;
    }

    if (!isDragging && translateX !== 0) {
      if (Math.abs(translateX) >= SWIPE_THRESHOLD) {
        // Mark that we've initiated delete to prevent re-running
        hasInitiatedDeleteRef.current = true;
        // Keep item swiped open, then trigger delete
        setTranslateX(-120);
        setIsDeleting(true);
        // Small delay to show the swiped state before deleting
        deleteTimerRef.current = setTimeout(async () => {
          try {
            await onSwipeDelete?.();
            // Success - item will be removed by parent component
          } catch (error) {
            // Error - snap back to original position
            setTranslateX(0);
            setIsDeleting(false);
            hasInitiatedDeleteRef.current = false;
            // Resume animation if it's the first item
            if (isFirstItem && contentRef.current) {
              resumeAnimationTimerRef.current = setTimeout(() => {
                if (contentRef.current) {
                  // Re-enable animation by removing inline style
                  contentRef.current.style.animation = '';
                }
              }, 500);
            }
          }
        }, 150);
      } else {
        // Snap back
        setTranslateX(0);
        // Resume animation if it's the first item after snapping back
        if (isFirstItem && contentRef.current) {
          if (resumeAnimationTimerRef.current) {
            clearTimeout(resumeAnimationTimerRef.current);
          }
          resumeAnimationTimerRef.current = setTimeout(() => {
            if (contentRef.current) {
              // Re-enable animation by removing inline style
              contentRef.current.style.animation = '';
            }
          }, 500);
        }
      }
    }
  }, [isDragging, translateX, onSwipeDelete, isFirstItem]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (deleteTimerRef.current) {
        clearTimeout(deleteTimerRef.current);
      }
      if (resumeAnimationTimerRef.current) {
        clearTimeout(resumeAnimationTimerRef.current);
      }
    };
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (!isMobile || isDeleting) {
      return;
    }

    // Stop animation completely when user starts interacting
    if (isFirstItem && contentRef.current) {
      contentRef.current.style.animation = 'none';
    }

    // Reset delete ref if user starts a new swipe
    if (hasInitiatedDeleteRef.current && translateX === 0) {
      hasInitiatedDeleteRef.current = false;
    }

    const touch = e.touches[0];

    setStartX(touch.clientX);
    setIsDragging(true);
  }, [isMobile, isDeleting, translateX, isFirstItem]);

  const handleTouchMove = useCallback((e) => {
    if (!isMobile || !isDragging) {
      return;
    }

    const touch = e.touches[0];
    const deltaX = touch.clientX - startX;

    // Only allow swiping left (negative deltaX)
    if (deltaX < 0) {
      const newTranslateX = Math.max(deltaX, -120);

      setTranslateX(newTranslateX);
    }
  }, [isMobile, isDragging, startX]);

  const handleTouchEnd = useCallback(() => {
    if (!isMobile || !isDragging) {
      return;
    }

    setIsDragging(false);

    // Resume animation after a delay if not dragging and it's the first item
    // Only resume if the item is back to its original position (not swiped)
    if (isFirstItem) {
      // Clear any existing resume timer
      if (resumeAnimationTimerRef.current) {
        clearTimeout(resumeAnimationTimerRef.current);
      }

      // If item is back to position 0, resume animation after delay
      if (translateX === 0 && contentRef.current) {
        resumeAnimationTimerRef.current = setTimeout(() => {
          if (contentRef.current) {
            // Re-enable animation by removing inline style
            contentRef.current.style.animation = '';
          }
        }, 500);
      }
    }
  }, [isMobile, isDragging, isFirstItem, translateX]);

  // Handle focus/blur for keyboard and mouse interactions
  const handleFocus = useCallback(() => {
    if (isFirstItem && isMobile && contentRef.current) {
      contentRef.current.style.animation = 'none';
    }
  }, [isFirstItem, isMobile]);

  const handleBlur = useCallback(() => {
    if (isFirstItem && isMobile) {
      // Clear any existing resume timer
      if (resumeAnimationTimerRef.current) {
        clearTimeout(resumeAnimationTimerRef.current);
      }
      // Small delay before resuming animation
      resumeAnimationTimerRef.current = setTimeout(() => {
        if (contentRef.current) {
          // Re-enable animation by removing inline style
          contentRef.current.style.animation = '';
        }
      }, 500);
    }
  }, [isFirstItem, isMobile]);

  // Disable swipe on desktop
  if (!isMobile) {
    return <Box>{children}</Box>;
  }

  return (
    <Box
      ref={containerRef}
      overflow="hidden"
      position="relative"
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      onTouchStart={handleTouchStart}
    >
      {/* Delete button background */}
      <Box
        {...styles.deleteButton}
        bottom={0}
        gap={2.5}
        position="absolute"
        right={0}
        top={0}
        transform={`translateX(${120 + translateX}px)`}
        transition={isDragging ? 'none' : 'transform 0.2s ease-out'}
        width="120px"
        zIndex={1}
      >
        {intl.formatMessage({
          defaultMessage: 'Remove',
          id: 'cart_flyout.swipeable_item.delete_button.remove',
        })}
      </Box>

      {/* Content */}
      <Box
        ref={contentRef}
        bg="white"
        className="swipeable-item-content"
        position="relative"
        tabIndex={isFirstItem ? 0 : -1}
        transform={translateX !== 0 ? `translateX(${translateX}px)` : 'translateX(0px)'}
        transition={isDragging ? 'none' : 'transform 0.2s ease-out'}
        zIndex={2}
        onBlur={handleBlur}
        onFocus={handleFocus}
      >
        {children}
      </Box>
    </Box>
  );
};

SwipeableItem.propTypes = {
  children: PropTypes.node.isRequired,
  onSwipeDelete: PropTypes.func.isRequired,
  isFirstItem: PropTypes.bool,
};

export default React.memo(SwipeableItem);
